import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FooterButton } from '@rivethealth/data/buttons';
import { TreeOption } from '@rivethealth/data/options';
import { AnalyticsEventOutput } from '../analytics/common-analytics.service';
import { AnchoredPosition } from '../callout/callout-position';
import { HookDirective } from '../core/hook';
import { useEffect, useMemo, useState } from '../core/hooks';
import { Loadable } from '../helpers/loadable';
import { getTreeOptionDisplay, getTreeOptionsDisplay } from './options';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'tree-multi-select-dropdown',
  styleUrls: ['./tree-multi-select-dropdown.component.scss'],
  templateUrl: './tree-multi-select-dropdown.component.html',
})
export class TreeMultiSelectDropdownComponent extends HookDirective {
  constructor(_cdr: ChangeDetectorRef) {
    super(_cdr);
  }

  @ViewChild('dropdown', { static: false }) dropdownRef: ElementRef;
  @ViewChild('callout', { static: false }) calloutRef: ElementRef;

  @Input()
  readonly analyticsEvents: {
    selected?: AnalyticsEventOutput<void>;
  } = {};

  @Input()
  showDefaultFooterButtons: boolean = true;

  //this can't be named options becuase the html has a component named options so we named it treeOptions
  @Input('options')
  treeOptions: TreeOption[] = [];

  @Input()
  disabled: boolean = false;

  @Input() error = false;
  @Input() warning = false;

  @Input()
  exact: boolean = false;

  @Input()
  filterable: boolean = false;

  @Input()
  placeholder: string | null = null;

  @Input()
  display: string | null = null;

  @Input()
  loadable: Loadable<any> | null = null;

  @Input()
  selectedOptions: string[] = [];

  @Input()
  leftButtons: FooterButton[] = [];

  @Input()
  rightButtons: FooterButton[] = [];

  @Input()
  disabledOptions: string[] = [];

  @Input()
  autoFocus: boolean = false;

  @Input()
  displayId: boolean;

  get dropdownWidth() {
    return this.dropdownRef.nativeElement.offsetWidth;
  }

  @Output()
  readonly selectedOptionsChange = new EventEmitter<string[]>();

  public open() {
    this._.setIsOpen(true);
  }

  public close() {
    this._.setIsOpen(false);
  }

  readonly topAndBottomPositions = [
    AnchoredPosition.BOTTOM_RIGHT,
    AnchoredPosition.BOTTOM_LEFT,
    AnchoredPosition.TOP_RIGHT,
    AnchoredPosition.TOP_LEFT,
  ];

  @HostListener('keyup', ['$event'])
  @HostListener('keydown', ['$event'])
  keyUp($event: any) {
    const key = $event.key;
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      $event.stopPropagation();
      $event.preventDefault();
      if ($event.type === 'keydown') {
        this._.setIsOpen(true);
      }
    }
  }

  get _() {
    const changeSelectedOptions = (newOptions: string[]) => {
      this.selectedOptionsChange.emit(newOptions);
      if (this.analyticsEvents.selected) {
        this.analyticsEvents.selected();
      }
    };

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const [position, setPosition] = useState<AnchoredPosition | null>(null);

    const positionedTop =
      position === AnchoredPosition.TOP_RIGHT ||
      position === AnchoredPosition.TOP_LEFT;

    const maxHeight = useMemo(() => {
      if (this.dropdownRef) {
        const dropdownRect: ClientRect =
          this.dropdownRef.nativeElement.getBoundingClientRect();

        if (!positionedTop) {
          return Math.min(400, window.innerHeight - dropdownRect.bottom);
        } else {
          return Math.min(400, dropdownRect.top);
        }
      }

      return 400;
    }, [positionedTop]);

    useEffect(() => {
      if (this.dropdownRef && isOpen) {
        this.dropdownRef.nativeElement.focus();
      }
    }, [isOpen]);

    const selectedCount = this.selectedOptions
      ? this.selectedOptions.length
      : 0;
    const selectedOptions = this.selectedOptions || [];

    const displayLeftButtons: FooterButton[] = useMemo(() => {
      const selectAllButton = {
        text: 'Select all',
        onClick: () =>
          changeSelectedOptions(
            getAllIds(this.treeOptions).map(id => id.toString()),
          ),
      };
      return this.showDefaultFooterButtons
        ? [...this.leftButtons, selectAllButton]
        : this.leftButtons;
    }, [this.leftButtons, this.treeOptions]);

    const displayRightButtons: FooterButton[] = useMemo(() => {
      const clearAllButton = {
        text: 'Clear',
        onClick: () => {
          changeSelectedOptions([]);
        },
      };
      return this.showDefaultFooterButtons
        ? [...this.rightButtons, clearAllButton]
        : this.rightButtons;
    }, [this.rightButtons]);

    const parent = this.treeOptions?.find(option =>
      option.children.some(child => child.id === selectedOptions[0]),
    );
    const parentSelected = this.treeOptions?.some(
      option => option.id === selectedOptions[0],
    );
    const selectedDisplay =
      this.display ??
      (selectedOptions.length === 1 &&
      (parent?.children.length != 1 || parentSelected)
        ? getTreeOptionDisplay(selectedOptions[0], this.treeOptions)
        : getTreeOptionsDisplay(selectedOptions, this.treeOptions));

    return {
      displayLeftButtons,
      displayRightButtons,
      selectedDisplay,
      selectedCount,
      isOpen,
      maxHeight,
      positionedTop,
      changeSelectedOptions,
      setIsOpen,
      setPosition,
    };
  }
}

function getAllIds(nodes: TreeOption[]): string[] {
  const selectedOptions: string[] = [];
  (function visit(nodes: TreeOption[]) {
    for (const node of nodes) {
      if (node.children.length) {
        visit(node.children);
      } else {
        selectedOptions.push(node.id);
      }
    }
  })(nodes);
  return selectedOptions;
}
